import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  accountingApi,
  formatInr,
  type AgeingBucketKey,
  type SubledgerAgeingReport,
} from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

type Kind = "AR" | "AP" | "DOCTOR" | "ADVANCES";

const kinds: Array<{ id: Kind; label: string }> = [
  { id: "AR", label: "AR (receivables)" },
  { id: "AP", label: "AP (vendors)" },
  { id: "DOCTOR", label: "Doctor payables" },
  { id: "ADVANCES", label: "Patient advances" },
];

const defaultBuckets: AgeingBucketKey[] = [
  "b0_30",
  "b31_60",
  "b61_90",
  "b91_180",
  "b180_plus",
];

export default function AccountingAgeingPage() {
  const { hospitalId } = useAccountingHospitalContext();
  const [kind, setKind] = useState<Kind>("AR");
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["accounting-ageing", hospitalId, kind, asOf],
    queryFn: () => accountingApi.subledgerAgeing(kind, hospitalId, asOf || undefined),
    enabled: !!hospitalId,
  });

  const report = data as SubledgerAgeingReport | undefined;
  const bucketKeys =
    report?.bucketDefs?.map((b) => b.key) ?? defaultBuckets;
  const bucketLabel = (key: AgeingBucketKey) =>
    report?.bucketDefs?.find((b) => b.key === key)?.label ?? key;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">AR / AP ageing</h2>
        <p className="mt-1 text-sm text-slate-500">
          Derived from posted voucher lines (FIFO open items). Not ops billing — books only.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {kinds.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => setKind(k.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              kind === k.id
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="w-44 space-y-1">
        <Label className="text-xs">As of</Label>
        <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </div>

      {report?.note ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {report.note}
          {report.controlAccounts?.length
            ? ` Controls: ${report.controlAccounts.map((a) => a.code).join(", ")}.`
            : null}
        </p>
      ) : null}

      {isLoading || isFetching ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Party</TableHead>
                {bucketKeys.map((k) => (
                  <TableHead key={k} className="text-right whitespace-nowrap">
                    {bucketLabel(k)}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Credit bal.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(report?.rows ?? []).map((row) => (
                <TableRow key={row.partyId}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{row.partyName}</div>
                    <div className="text-xs text-slate-400">
                      {[row.partyCode, row.partyType, row.accountCodes.join("/")].filter(Boolean).join(" · ")}
                    </div>
                  </TableCell>
                  {bucketKeys.map((k) => (
                    <TableCell key={k} className="text-right tabular-nums">
                      {row.buckets[k] ? formatInr(row.buckets[k]) : "—"}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatInr(row.total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">
                    {row.creditBalance ? formatInr(row.creditBalance) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {report?.totals ? (
                <TableRow className="bg-slate-50 font-medium">
                  <TableCell>
                    Totals ({report.totals.partyCount} parties)
                  </TableCell>
                  {bucketKeys.map((k) => (
                    <TableCell key={k} className="text-right tabular-nums">
                      {formatInr(report.totals.buckets[k] ?? 0)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums">
                    {formatInr(report.totals.total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(report.totals.creditBalance)}
                  </TableCell>
                </TableRow>
              ) : null}
              {!report?.rows?.length ? (
                <TableRow>
                  <TableCell colSpan={bucketKeys.length + 3} className="text-sm text-slate-500">
                    No open balances as of {asOf || "today"}.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
